import {APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {AppRoutingModule} from './app-routing.module';
import {RouterModule} from '@angular/router';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
/** Services */
import {UserService} from './services/user.service';
import {ChannelsService} from "./services/channels.service";
import {AuthInterceptor} from "./services/auth-intercept.service";
import {AppHttpInterceptor} from "./services/response-intercept.service";
import {MetricsService} from "./services/metrics.service";
import {CookieService} from "./services/cookie.service";
import {PromptUpdateService} from "./services/prompt-update.service";
/** Components */
import {AppComponent} from './app.component';
import {NavigationComponent} from './components/navigation/navigation.component';
import {TimelineComponent} from './components/timeline/timeline.component';
import {CalendarComponent} from './components/calendar/calendar.component';
import {HomePageComponent} from './components/home-page/home-page.component';
import {LoginComponent} from "./components/login/login.component";
import {LogoutComponent} from "./components/logout/logout.component";
import {ChannelComponent} from "./components/channel/channel.component";
import {ChannelListComponent} from "./components/channel-list/channel-list.component";
import {RecommenderComponent} from './components/recommender/recommender.component';
import { ErrorpageComponent } from './components/errorpage/errorpage.component';
import { NotificationComponent } from './components/notification/notification.component';
/** Extras */
import {SafeUrlPipe} from "./pipes/safe-url.pipe";
import {RemovewhitespacesPipe} from "./pipes/remove-white-sapces.pipe";
import {KeysPipe} from "./pipes/keys.pipe";
import {FormatHtmlPipe} from './pipes/format-html.pipe';
import {ServiceWorkerModule, SwUpdate} from '@angular/service-worker';
import {environment} from '../environments/environment';
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import { SearchComponent } from './components/search/search.component';
import {NgbAccordionBody, NgbAccordionButton, NgbAccordionCollapse, NgbAccordionDirective, NgbAccordionHeader, NgbAccordionItem, NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, NgbPagination, NgbPopoverModule} from "@ng-bootstrap/ng-bootstrap";
import {DigitalAssistantComponent} from "./components/digital-assistant/digital-assistant.component";
import {MarkdownModule, MARKED_OPTIONS, MarkedOptions, MarkedRenderer, provideMarkdown} from "ngx-markdown";
import {FeedbackComponent} from "./components/feedback/feedback.component";
import {UserNotificationsComponent} from "./components/user-notifications/user-notifications.component";
import {AnnouncementViewComponent} from "./components/announcement-view/announcement-view.component";
// import {NgbToast} from "@ng-bootstrap/ng-bootstrap";
// import {ToastsContainer} from "./components/toast-global/toasts-container.component";
// import {NgbdToastGlobal} from "./components/toast-global/toast-global.component";
// Required for MSAL
import {PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel, PromptValue} from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MsalGuardConfiguration, MsalRedirectComponent } from '@azure/msal-angular';
import {MsalAuthService} from "./services/msal-auth.service";
import {firstValueFrom} from "rxjs";
import { OAuthSettings } from "./app-config.service";
import {JoinPipe} from "./pipes/join.pipe";
import {MyClassesComponent} from "./components/my-classes/my-classes.component";
import {FormatTimePipe} from "./pipes/time-format.pipe";

// export const OAuthSettings = {
//     appId: '56da94bb-de3c-4cd2-8221-1eacd6053d2f',
//     tenant: 'daa6c3c0-e283-47e5-85e2-bddf85a39c03',
//     redirectUri: 'https://vpfr-mj0jxvdb.usask.ca:4200/msal',
//     scopes: ['User.Read', 'Calendars.Read'],
//     prompt: PromptValue.SELECT_ACCOUNT,
//     loginHint: "test.usask.ca"
// };
export const markedOptionsFactory = (): MarkedOptions => {
    const renderer = new MarkedRenderer();

    renderer.link = ({ href, title, text }): string => {
        return '<a target="_blank" href="'+href+'">' + text + '</a>';
    };
    return {
        renderer: renderer,
        gfm: true,
        breaks: true,
        pedantic: false
    };
}
// export const checkForUpdates = (swUpdate: SwUpdate): (() => Promise<any>) => {
//     return (): Promise<void> =>
//         new Promise((resolve) => {
//             swUpdate.checkForUpdate();
//
//             swUpdate.versionUpdates.subscribe(() => {
//                 window.location.reload();
//             });
//
//             resolve();
//         });
// };

// Factory to create your PCA (same as you already have)
export function MSALInstanceFactory() {
    return new PublicClientApplication({
        auth: {
            clientId: OAuthSettings.appId,
            authority: `https://login.microsoftonline.com/${OAuthSettings.tenant}`,
            redirectUri: OAuthSettings.redirectUri, // e.g. https://vpfr-mj0jxvdb.usask.ca:4200/msal
            postLogoutRedirectUri: '/',
        },
        cache: {
            cacheLocation: BrowserCacheLocation.SessionStorage,
        },
        system: {
            allowPlatformBroker: false,
            loggerOptions: {
                loggerCallback: (level, message) => console.log('[MSAL]', message),
                piiLoggingEnabled: false,
                logLevel: LogLevel.Error,
            },
        },
    });
}
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set("https://graph.microsoft.com/*", ['User.Read']);
    protectedResourceMap.set("https://graph.microsoft.com/v1.0/me/events", ['Calendars.ReadBasic']);
    protectedResourceMap.set("https://graph.microsoft.com/v1.0/me/calendarView", ['Calendars.ReadBasic']);
    protectedResourceMap.set("https://vpfr-mj0jxvdb.usask.ca:4200", ['User.Read','Calendars.ReadBasic']);
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}
// Ensure MSAL is initialized before app runs
// { provide: APP_INITIALIZER, useFactory: initializeMsalFactory, deps: [MsalService], multi: true },
export function initializeMsalFactory(msalService: MsalService) {
    return () => firstValueFrom(msalService.initialize());
}
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: ['User.Read'],
        },
    };
}

@NgModule({
    declarations: [
        AppComponent,
        NavigationComponent,
        TimelineComponent,
        CalendarComponent,
        HomePageComponent,
        LoginComponent,
        LogoutComponent,
        ChannelComponent,
        ChannelListComponent,
        SafeUrlPipe,
        RemovewhitespacesPipe,
        KeysPipe,
        RecommenderComponent,
        FormatHtmlPipe,
        ErrorpageComponent,
        NotificationComponent,
        SearchComponent,
        AnnouncementViewComponent
    ],
    bootstrap: [
        AppComponent,
        MsalRedirectComponent
    ],
    exports: [
        FormatHtmlPipe
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA
    ],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		RouterModule,
		ReactiveFormsModule,
		FormsModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: environment.hasServiceWorker,
			registrationStrategy: 'registerImmediately'
		}),
		NgbPagination,
		NgbAccordionDirective,
		NgbAccordionItem,
		NgbAccordionHeader,
		NgbAccordionButton,
		NgbAccordionCollapse,
		NgbAccordionBody,
		NgbDropdown,
		NgbDropdownMenu,
		NgbDropdownToggle,
		NgbPopoverModule,
		DigitalAssistantComponent,
		MarkdownModule.forRoot(),
		FeedbackComponent,
		UserNotificationsComponent,
		MsalModule.forRoot(
			MSALInstanceFactory(),
			MSALGuardConfigFactory(),
			MSALInterceptorConfigFactory()
		),
		JoinPipe,
		MyClassesComponent,
		FormatTimePipe
	], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        MsalAuthService,
        { provide: 'Window', useValue: window },
        UserService,
        ChannelsService,
        MetricsService,
        CookieService,
        PromptUpdateService,
        // CheckForUpdateService,
        // LogUpdateService,
        CacheStorage,
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AppHttpInterceptor, multi: true },
        provideMarkdown({ markedOptions: { provide: MARKED_OPTIONS, useFactory: markedOptionsFactory } }),
        // { provide: APP_INITIALIZER, useFactory: checkForUpdates, multi: true, deps: [SwUpdate /* Add whatever dependency is required here */] },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule { }
